/**
 * IMAI-owned test workspaces whose analytics stay captured but tagged, so
 * main dashboards filter them out and a dedicated dashboard watches them
 * (#3530). Lune Atelier ("Lune") is the production smoke-test workspace.
 * Shared by the browser PostHog client and the Convex capture path.
 */
export const LUNE_WORKSPACE_SLUG = 'lune-atelier';

export const INTERNAL_TEST_WORKSPACE_SLUGS: readonly string[] = [
  LUNE_WORKSPACE_SLUG,
];

export const INTERNAL_TEST_PROPERTY = 'internal_test';
export const INTERNAL_TEST_WORKSPACE_PROPERTY = 'workspace';

export type InternalTestAnalyticsProperties = {
  [INTERNAL_TEST_PROPERTY]: true;
  [INTERNAL_TEST_WORKSPACE_PROPERTY]: string;
};

export function isInternalTestWorkspaceSlug(
  slug: string | null | undefined
): boolean {
  return (
    typeof slug === 'string' && INTERNAL_TEST_WORKSPACE_SLUGS.includes(slug)
  );
}

export function internalTestAnalyticsProperties(
  slug: string
): InternalTestAnalyticsProperties {
  return {
    [INTERNAL_TEST_PROPERTY]: true,
    [INTERNAL_TEST_WORKSPACE_PROPERTY]: slug,
  };
}

/**
 * Trigger run tag carrying the server-derived internal test marker. Only the
 * Convex enqueue path sets it, from the same flagged-workspace source as the
 * Convex captures, so the worker trusts it without a list of its own.
 */
export const INTERNAL_TEST_RUN_TAG_PREFIX = 'internal_test:';

export function internalTestRunTag(
  properties: Partial<InternalTestAnalyticsProperties>
): string | null {
  const slug = properties[INTERNAL_TEST_WORKSPACE_PROPERTY];
  return properties[INTERNAL_TEST_PROPERTY] === true && slug
    ? `${INTERNAL_TEST_RUN_TAG_PREFIX}${slug}`
    : null;
}

export function internalTestPropertiesFromRunTags(
  tags: readonly string[] | undefined
): InternalTestAnalyticsProperties | Record<string, never> {
  const slug = tags
    ?.find((tag) => tag.startsWith(INTERNAL_TEST_RUN_TAG_PREFIX))
    ?.slice(INTERNAL_TEST_RUN_TAG_PREFIX.length);
  return slug ? internalTestAnalyticsProperties(slug) : {};
}

/** Workspace routes are `/<orgSlug>/...`; returns the slug when internal. */
export function internalTestWorkspaceSlugFromPath(
  pathname: string | null | undefined
): string | null {
  const firstSegment = pathname?.split('/').find(Boolean);
  if (!firstSegment) return null;
  const slug = firstSegment.toLowerCase();
  return isInternalTestWorkspaceSlug(slug) ? slug : null;
}

export interface InternalTestSuperPropertyClient {
  register(properties: Record<string, unknown>): void;
  unregister(property: string): void;
}

/**
 * Browser half of the tagging: while the route is an internal test
 * workspace, every event carries the tag as super properties; leaving the
 * workspace removes them. The tag stays event-scoped and never touches the
 * person, so a real user who visits Lune keeps an untagged person. Only the
 * dedicated Lune logins are tagged at person level, by the seed. Returns a
 * route-change callback.
 */
export function createInternalTestWorkspaceSync(
  client: InternalTestSuperPropertyClient
): (pathname: string) => string | null {
  let activeSlug: string | null = null;
  return (pathname) => {
    const slug = internalTestWorkspaceSlugFromPath(pathname);
    if (slug === activeSlug) return slug;
    activeSlug = slug;
    if (slug) {
      client.register(internalTestAnalyticsProperties(slug));
    } else {
      client.unregister(INTERNAL_TEST_PROPERTY);
      client.unregister(INTERNAL_TEST_WORKSPACE_PROPERTY);
    }
    return slug;
  };
}
